import type React from "react";
import {
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from "react";
import { useTranslation } from "@/packages/utils";
// Logger type is used in ErrorBoundaryProps
import type {
  ErrorBoundaryProps,
  FallbackProps,
  ErrorBoundaryState,
  ErrorContext,
  ErrorInfo,
  ErrorBoundaryContextValue,
} from "./types";
import {
  generateCorrelationId,
  getComponentName,
  shouldIsolateError,
  reportErrorDetails,
  createErrorContext,
  isDevelopment,
} from "./utils";

// Sentry integration (optional - only if Sentry is available)
declare global {
  interface Window {
    Sentry?: {
      captureException: (
        error: Error,
        options?: Record<string, unknown>,
      ) => string;
    };
  }
}

// Error boundary context
export const ErrorBoundaryContext =
  createContext<ErrorBoundaryContextValue | null>(null);

export const useErrorBoundary = () => {
  const context = useContext(ErrorBoundaryContext);

  if (!context) {
    throw new Error("useErrorBoundary must be used within an ErrorBoundary");
  }

  return context;
};

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallback: FallbackComponent = DefaultFallbackComponent,
  onError,
  onReset,
  enableRetry = true,
  maxRetries = 3,
  // retryDelay = 1000,
  isolateErrors = false,
  errorCategories,
  logger,
}) => {
  const [state, setState] = useState<ErrorBoundaryState>({
    hasError: false,
    error: null,
    errorInfo: null,
    context: null,
    retryCount: 0,
    isRetrying: false,
  });

  const handleErrorCallback = useCallback(
    (error: Error, errorInfo: ErrorInfo) => {
      const correlationId =
        logger?.generateCorrelationId() || generateCorrelationId();

      const context: ErrorContext = createErrorContext(
        "default",
        getComponentName(errorInfo),
        window.location.pathname,
      );

      // Log error with full context
      logger?.error("React Error Boundary caught an error", {
        error,
        correlationId,
        componentStack: errorInfo.componentStack,
        context,
      });

      // Check if error should be isolated
      if (isolateErrors && shouldIsolateError(error, errorCategories)) {
        logger?.info(
          "Error isolated, not propagating to global error handler",
          {
            correlationId,
            error: error.message,
          },
        );
      }

      // Report to Sentry if available (skip in development)
      if (!isDevelopment() && window.Sentry) {
        window.Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
            error: context,
          },
          tags: {
            correlationId,
            errorBoundary: context.errorBoundary,
            isolated: String(isolateErrors),
          },
        });
      }

      // Call custom error handler
      onError?.(error, errorInfo, context);

      // Update state
      setState({
        hasError: true,
        error,
        errorInfo,
        context,
        retryCount: 0,
        isRetrying: false,
      });
    },
    [logger, onError, isolateErrors, errorCategories],
  );

  const resetError = useCallback(() => {
    setState({
      hasError: false,
      error: null,
      errorInfo: null,
      context: null,
      retryCount: 0,
      isRetrying: false,
    });

    onReset?.();
  }, [onReset]);

  // // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const _retryError = useCallback(async () => {
  //   if (!enableRetry || state.retryCount >= maxRetries) {
  //     return;
  //   }

  //   setState((prev) => ({ ...prev, isRetrying: true }));

  //   // Wait for retry delay
  //   await new Promise((resolve) => setTimeout(resolve, retryDelay));

  //   setState((prev) => ({
  //     ...prev,
  //     hasError: false,
  //     isRetrying: false,
  //     retryCount: prev.retryCount + 1,
  //   }));
  // }, [enableRetry, maxRetries, retryDelay, state.retryCount]);

  // Error boundary effect
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const errorInfo: ErrorInfo = {
        componentStack: "",
      };
      handleErrorCallback(event.error || new Error(event.message), errorInfo);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorInfo: ErrorInfo = {
        componentStack: "",
      };
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      handleErrorCallback(error, errorInfo);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, [handleErrorCallback]);

  const contextValue: ErrorBoundaryContextValue = {
    error: state.error,
    resetBoundary: resetError,
  };

  if (state.hasError && state.error && state.errorInfo && state.context) {
    const canRetry = enableRetry && state.retryCount < maxRetries;

    return (
      <ErrorBoundaryContext.Provider value={contextValue}>
        <FallbackComponent
          error={state.error}
          errorInfo={state.errorInfo}
          context={state.context}
          resetError={resetError}
          retryCount={state.retryCount}
          canRetry={canRetry}
          correlationId={state.context.correlationId}
        />
      </ErrorBoundaryContext.Provider>
    );
  }

  return (
    <ErrorBoundaryContext.Provider value={contextValue}>
      {children}
    </ErrorBoundaryContext.Provider>
  );
};

// Default fallback component
const DefaultFallbackComponent: React.FC<FallbackProps> = ({
  error,
  errorInfo,
  context,
  resetError,
  retryCount: _retryCount,
  canRetry,
  correlationId,
}) => {
  const { t } = useTranslation(["common"]);
  const [showDetails, setShowDetails] = useState(false);
  const [isReporting, setIsReporting] = useState(false);

  const handleReportError = async () => {
    setIsReporting(true);
    try {
      await reportErrorDetails(error, errorInfo, context);
    } catch (reportError) {
      console.error("Failed to report error:", reportError);
    } finally {
      setIsReporting(false);
    }
  };

  return (
    <div
      className="error-boundary-fallback min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4"
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <svg
              className="h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              role="img"
              aria-label="Error icon"
            >
              <title>Error</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <div className="error-message">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t("common:errorBoundary.title")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t("common:errorBoundary.message")}
            </p>

            {correlationId && (
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
                {t("common:errorBoundary.errorId")}{" "}
                <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                  {correlationId}
                </code>
              </p>
            )}
          </div>

          <div className="error-actions space-y-3">
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                type="button"
                onClick={resetError}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                {t("common:errorBoundary.tryAgain")}
              </button>

              {canRetry && (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  {t("common:errorBoundary.reloadPage")}
                </button>
              )}

              <button
                type="button"
                onClick={handleReportError}
                disabled={isReporting}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 transition-colors"
              >
                {isReporting
                  ? t("common:errorBoundary.reporting")
                  : t("common:errorBoundary.reportIssue")}
              </button>
            </div>

            {isDevelopment() && (
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {showDetails
                  ? t("common:errorBoundary.hideDetails")
                  : t("common:errorBoundary.showDetails")}
              </button>
            )}
          </div>

          {showDetails && isDevelopment() && (
            <details className="error-details mt-4 text-left">
              <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t("common:errorBoundary.errorDetails")}
              </summary>
              <div className="mt-2 p-4 bg-gray-100 dark:bg-gray-700 rounded-md overflow-auto max-h-60">
                <div className="mb-2">
                  <strong className="text-xs">Error:</strong>
                  <p className="text-xs font-mono break-all">{error.message}</p>
                </div>
                <div className="mb-2">
                  <strong className="text-xs">Stack:</strong>
                  <pre className="whitespace-pre-wrap text-xs font-mono overflow-x-auto">
                    {error.stack}
                  </pre>
                </div>
                <div className="mb-2">
                  <strong className="text-xs">Component Stack:</strong>
                  <pre className="whitespace-pre-wrap text-xs font-mono overflow-x-auto">
                    {errorInfo.componentStack}
                  </pre>
                </div>
                <div>
                  <strong className="text-xs">Context:</strong>
                  <pre className="whitespace-pre-wrap text-xs font-mono overflow-x-auto">
                    {JSON.stringify(context, null, 2)}
                  </pre>
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
